/* ============================================================
 * Tablero del día.
 *
 * El original calculaba la ocupación a partir de un objeto en memoria que
 * "materializaba" reservas inventadas con una función hash. Ahora el
 * tablero llega del backend, que además lo anonimiza cuando quien mira es
 * Aleph: esas filas no traen nombre ni teléfono porque la API no los envía,
 * no porque el frontend los oculte.
 * ============================================================ */

import React from 'react';
import { useCtx, useRecurso, api } from '../estado.jsx';
import { Kpi, Recurso, SedeTabs } from './Comunes.jsx';
import { DOW, addDaysIso, isoOffset, bloquesDelDia, etiquetaVisitas } from '../utiles/fechas.js';

function DayStrip() {
  const { boardDate, setBoardDate } = useCtx();
  const d0 = new Date(boardDate + 'T00:00:00');
  const dow = (d0.getDay() + 6) % 7;
  const lunes = addDaysIso(boardDate, -dow);
  const hoy = isoOffset(0);
  const mesRaw = d0.toLocaleDateString('es-CL', { month: 'long' });
  const mesLabel = mesRaw.charAt(0).toUpperCase() + mesRaw.slice(1) + ' ' + d0.getFullYear();
  const dias = Array.from({ length: 7 }, (_, i) => addDaysIso(lunes, i));

  return (
    <div className="day-strip">
      <div className="strip-nav">
        <button className="strip-btn" onClick={() => setBoardDate(addDaysIso(boardDate, -7))}>‹</button>
        <span className="strip-month">{mesLabel}</span>
        <button className="strip-btn" onClick={() => setBoardDate(addDaysIso(boardDate, 7))}>›</button>
        <button className="strip-today" onClick={() => setBoardDate(hoy)}>Hoy</button>
        <label className="strip-jump">Ir a
          <input type="date" value={boardDate}
                 onChange={(e) => e.target.value && setBoardDate(e.target.value)} />
        </label>
      </div>
      <div className="day-row">
        {dias.map((iso) => {
          const d = new Date(iso + 'T00:00:00');
          return (
            <button key={iso} className={'day' + (iso === hoy ? ' today' : '')}
                    aria-pressed={iso === boardDate} onClick={() => setBoardDate(iso)}>
              <div className="dow">{DOW[d.getDay()]}</div>
              <div className="dnum">{d.getDate()}</div>
              <span className="ddot" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TableroView() {
  const { session, sedeId, boardDate, openModal } = useCtx();
  const esAleph = session.rol === 'aleph';

  const sede = useRecurso(() => api.sede(sedeId), [sedeId]);
  const tablero = useRecurso(() => api.tablero(sedeId, boardDate), [sedeId, boardDate]);

  return (
    <div className="view on">
      <SedeTabs id={sedeId} active="sede" />
      <Recurso estado={sede} que="la sede">
        {(s) => {
          const equipos = s.tunes || [];
          const activos = equipos.filter((t) => t.estado === 'disponible');
          const horas = bloquesDelDia(s.hora_apertura, s.hora_cierre, s.bloque_min);
          const grid = { gridTemplateColumns: '62px repeat(' + equipos.length + ',1fr)' };

          // Índice 'tuneId|hora' → reserva, para pintar cada celda sin recorrer
          // la lista entera en cada una.
          const porCelda = {};
          for (const r of (tablero.datos || [])) porCelda[r.tune_id + '|' + r.hora] = r;

          const ocupadas = (tablero.datos || []).length;
          const total = horas.length * activos.length;
          const ocupacion = total ? Math.round(ocupadas / total * 100) : 0;

          return (
            <>
              <div className="h-eyebrow">{s.comuna} · Panel de sede</div>
              <h1 className="h-title">{s.nombre}</h1>
              <p className="h-sub">
                {esAleph
                  ? 'Vista de ocupación · solo lectura. Aleph no ve nombres de cliente.'
                  : 'Tablero del día · una columna por equipo TUNE. Pincha un hueco para reservar o una reserva para liberarla.'}
              </p>

              <DayStrip />

              <div className="kpis">
                <Kpi label="Reservas del día" val={ocupadas} foot={`de ${total} bloques-equipo`} />
                <Kpi label="Ocupación" val={ocupacion} sufijo="%" foot="equipos activos" />
                <Kpi label="Equipos activos" val={activos.length} sufijo={`/${equipos.length}`} foot="disponibles" />
                <Kpi label="Precio sesión" val={(s.precio_tune || 0).toLocaleString('es-CL')} sufijo=" CLP" foot="lo fija Aleph" />
              </div>

              <h2 className="section-h">Tablero</h2>
              <Recurso estado={tablero} que="el tablero">
                {() => (
                  <div className="board-wrap"><div className="board">
                    <div className="board-head" style={grid}>
                      <div className="bh-cell time">Hora</div>
                      {equipos.map((t) => (
                        <div key={t.id} className="bh-cell">
                          {t.etiqueta}
                          {t.estado === 'mantenimiento'
                            ? <span style={{ color: 'var(--gold)' }}> · mant.</span> : null}
                        </div>
                      ))}
                    </div>

                    {horas.map((hora) => (
                      <div key={hora} className="board-row" style={grid}>
                        <div className="bc time">{hora}</div>
                        {equipos.map((t) => {
                          const r = porCelda[t.id + '|' + hora];

                          // Un equipo en mantenimiento no admite reservas nuevas,
                          // pero si le queda alguna hay que SEGUIR VIÉNDOLA. Antes
                          // se pintaba la celda vacía y la reserva desaparecía de
                          // la vista aunque siguiera viva: el cliente se presentaba
                          // y nadie sabía nada.
                          if (t.estado === 'mantenimiento') {
                            if (!r) return <div key={t.id} className="bc mant">—</div>;
                            return (
                              <div key={t.id} className="bc busy"
                                   style={{ outline: '2px solid var(--err)', outlineOffset: '-2px' }}
                                   onClick={() => openModal({ tipo: 'detalle', reserva: r, hora })}>
                                <div className="bn">{r.nombre}</div>
                                <div className="bv" style={{ color: 'var(--err)' }}>equipo parado</div>
                              </div>
                            );
                          }
                          if (r) {
                            if (esAleph) {
                              return (
                                <div key={t.id} className="bc busy"><div className="bn">Ocupado</div></div>
                              );
                            }
                            return (
                              <div key={t.id} className="bc busy"
                                   onClick={() => openModal({ tipo: 'detalle', reserva: r, hora })}>
                                <div className="bn">{r.nombre}</div>
                                <div className="bv">{etiquetaVisitas(r.visitas)}</div>
                              </div>
                            );
                          }
                          if (esAleph) return <div key={t.id} className="bc free" />;
                          return (
                            <div key={t.id} className="bc free"
                                 onClick={() => openModal({ tipo: 'reserva', sede: s, hora })} />
                          );
                        })}
                      </div>
                    ))}
                  </div></div>
                )}
              </Recurso>
            </>
          );
        }}
      </Recurso>
    </div>
  );
}
